import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import {
  useDocumentUpload,
  IDocumentType,
  IDocumentTypeOption,
  IPrefilledAttachment,
  IUploadedFile,
} from 'Hooks/useDocumentUpload';
import { FileStatus, DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { isValidUploadFile } from 'Utils';
import { useFileUploadHandlers } from '../../Modules/UniversalRequest/FileUpload/DocumentUploadManager/Hooks/useFileUploadHandlers';

export interface IDocumentUploadContextValue {
  availableDocumentTypes: IDocumentTypeOption[];
  documentTypes: IDocumentType[];
  availableTypes: IDocumentTypeOption[];
  hasSelectedTypes: boolean;
  showDeleteButton: boolean;
  addDocumentType: (type: IDocumentTypeOption) => void;
  toggleCollapse: (typeId: string) => void;
  handleDelete: (typeId: string) => void;
  handleFileSelect: (typeId: string, files: FileList) => void;
  handleDeleteFile: (typeId: string, fileId: string) => void;
  handlePreviewFile: (typeId: string, fileId: string) => void;
  showModal: boolean;
  toastQueue: { id: string; message: string }[];
  pendingDeleteType: IDocumentType | null;
  showPreviewModal: boolean;
  previewFile: {
    fileName: string;
    base64: string;
    fileExtension: string;
  } | null;
  handleUndoToast: (toastId: string) => void;
  handleCloseToast: (toastId: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleClosePreview: () => void;
  fileValidationErrorByTypeId: Record<string, string | null>;
}

const DocumentUploadContext = createContext<IDocumentUploadContextValue | null>(
  null,
);

export const useDocumentUploadContext = () => {
  const ctx = useContext(DocumentUploadContext);
  if (!ctx) {
    throw new Error(
      'useDocumentUploadContext must be used within DocumentUploadProvider',
    );
  }
  return ctx;
};

interface IDocumentUploadProviderProps {
  availableDocumentTypes: IDocumentTypeOption[];
  onUploadComplete?: (documentTypes: any[]) => void;
  prefilledAttachment?: IPrefilledAttachment;
  children: React.ReactNode;
}

export const DocumentUploadProvider = ({
  availableDocumentTypes,
  onUploadComplete,
  prefilledAttachment,
  children,
}: IDocumentUploadProviderProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const {
    documentTypes,
    addDocumentType,
    addDocumentTypeWithFiles,
    removeDocumentType,
    toggleCollapse,
    addFile,
    removeFile,
    updateFileProgress,
    updateFileStatus,
    getAvailableTypes,
  } = useDocumentUpload([], onUploadComplete);

  const handlers = useFileUploadHandlers({
    documentTypes,
    addDocumentType,
    addDocumentTypeWithFiles,
    removeDocumentType,
    addFile,
    removeFile,
    updateFileProgress,
    updateFileStatus,
  });

  const availableTypes = useMemo(
    () => getAvailableTypes(availableDocumentTypes),
    [getAvailableTypes, availableDocumentTypes, documentTypes],
  );

  const hasSelectedTypes = documentTypes.length > 0;
  const showDeleteButton = availableDocumentTypes.length > 1;

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current || availableDocumentTypes.length === 0) {
      return;
    }

    if (prefilledAttachment?.file) {
      const file = prefilledAttachment.file;

      if (isValidUploadFile(file)) {
        const invoiceLabel = dictionary.uploadFile.invoiceDocumentLabel;
        const invoiceType =
          availableDocumentTypes.find((type) =>
            type.label.includes(invoiceLabel),
          ) ?? availableDocumentTypes[0];
        const uploadedFile: IUploadedFile = {
          id: crypto.randomUUID(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadProgress: 100,
          status: FileStatus.SUCCESS,
          file,
        };

        hasInitialized.current = true;
        addDocumentTypeWithFiles(invoiceType, [uploadedFile]);
        return;
      }
    }

    if (availableDocumentTypes.length === 1 && availableTypes.length === 1) {
      hasInitialized.current = true;
      addDocumentType(availableTypes[0]);
    }
  }, [
    availableDocumentTypes,
    availableTypes,
    addDocumentType,
    addDocumentTypeWithFiles,
    prefilledAttachment,
    dictionary.uploadFile.invoiceDocumentLabel,
  ]);

  const value: IDocumentUploadContextValue = {
    availableDocumentTypes,
    documentTypes,
    availableTypes,
    hasSelectedTypes,
    showDeleteButton,
    addDocumentType,
    toggleCollapse,
    handleDelete: handlers.handleDelete,
    handleFileSelect: handlers.handleFileSelect,
    handleDeleteFile: handlers.handleDeleteFile,
    handlePreviewFile: handlers.handlePreviewFile,
    showModal: handlers.showModal,
    toastQueue: handlers.toastQueue.map((t) => ({
      id: t.id,
      message: t.message,
    })),
    pendingDeleteType: handlers.pendingDeleteType,
    showPreviewModal: handlers.showPreviewModal,
    previewFile: handlers.previewFile,
    handleUndoToast: handlers.handleUndoToast,
    handleCloseToast: handlers.handleCloseToast,
    handleConfirmDelete: handlers.handleConfirmDelete,
    handleCancelDelete: handlers.handleCancelDelete,
    handleClosePreview: handlers.handleClosePreview,
    fileValidationErrorByTypeId: handlers.fileValidationErrorByTypeId,
  };

  return (
    <DocumentUploadContext.Provider value={value}>
      {children}
    </DocumentUploadContext.Provider>
  );
};
